import type { PODApi as PODMallAutoOrderApi } from '@mathflat/domain/@entities/PODMall/KMMAutoOrder/api'
import type { PODApi as PODMallCartItemApi } from '@mathflat/domain/@entities/PODMall/CartItem/api'
import type { PODApi as PODMallCouponApi } from '@mathflat/domain/@entities/PODMall/Coupon/api'
import type { PODApi as PODMallOrderApi } from '@mathflat/domain/@entities/PODMall/Order/api'
import type { OrderStatusType } from '@mathflat/domain/@entities/PODMall/Order/type'
import type { PODApi as PODMallPaymentApi } from '@mathflat/domain/@entities/PODMall/Payment/api'
import type { PODApi as PODMallPreOrderApi } from '@mathflat/domain/@entities/PODMall/PreOrder/api'
import type { PodProductApi } from '@mathflat/domain/@entities/PODMall/Product/api'
import type { Entity as ProductEntity } from '@mathflat/domain/@entities/PODMall/Product/dto'
import type { Entity as ProductDetailEntity } from '@mathflat/domain/@entities/PODMall/ProductDetail/dto'
import type { PODApi as PODMallSettlementApi } from '@mathflat/domain/@entities/PODMall/Settlement/api'
import type { PODMallUserApi } from '@mathflat/domain/@entities/PODMall/User/api'

import type { ParentPaymentValidationDTO } from '@/page/ParentPayment/service/dto'
import type { PaginationResponse } from '@/types/common/api'
import { identifyAxios } from '@/utils/axios/identification/identifyAxios'
import maxios from '@/utils/axios/maxios'
import podAxios from '@/utils/axios/podAxios'

const podMallApi = {
  // -- Product
  async getSignatureWorkbookProductList(params: PodProductApi.Request.SignatureWorkbookProduct) {
    const { data } = await this.getProducts<'SIGNATURE_WORKBOOK'>(params)

    return data
  },

  async getKMMProductList(params: PodProductApi.Request.KMMProduct) {
    const { data } = await podAxios.get<PaginationResponse<ProductEntity.KMMProduct[]>>(
      '/product',
      {
        params,
      },
    )
    return data
  },

  async getSignatureWorkbookProductDetail({
    id,
    type,
  }: PodProductApi.Request.SignatureWorkbookDetail) {
    const { data } = await podAxios.get<ProductDetailEntity.ProductDetail>(`/product/${id}`, {
      params: { type },
    })
    return data
  },

  getProducts: async <T extends PodProductApi.Response.Product['type']>(
    params: PodProductApi.Request.Product<T>,
  ) => {
    return await podAxios.get<
      PaginationResponse<
        T extends 'SIGNATURE_WORKBOOK'
          ? PodProductApi.Response.SignatureWorkbookProduct[]
          : T extends 'CUSTOM_WORKBOOK'
            ? PodProductApi.Response.CustomWorkbookProduct[]
            : PodProductApi.Response.PrintDocumentProduct[]
      >
    >('/product', {
      params,
    })
  },

  getExpectedPrice: async (
    productId: PodProductApi.Response.Product['id'],
    params: PodProductApi.Request.ExpectedAmount,
  ) => {
    return await podAxios.get<PodProductApi.Response.ExpectedAmount>(
      `/product/${productId}/expected-amount`,
      {
        params,
      },
    )
  },

  // -- User
  async getUser() {
    const { data } = await podAxios.get<PODMallUserApi.Response.UserResponse>('/user')
    return data
  },

  async putUserDeliveryAddress(payload: PODMallUserApi.Request.UserDeliveryRequest) {
    return await podAxios.put('/user/delivery-address', payload)
  },

  // -- CartItem
  async getCartItems(params?: PODMallCartItemApi.Request.CartItemParams) {
    const { data } = await podAxios.get<PODMallCartItemApi.Response.CartItems>('/cart-item', {
      params,
    })
    return data
  },

  async putCartItem(
    id: PODMallCartItemApi.Request.CartItemId,
    payload:
      | PODMallCartItemApi.Request.SignatureWorkbookCartItemModification
      | PODMallCartItemApi.Request.CustomWorkbookCartItemModification
      | PODMallCartItemApi.Request.PrintDocumentCartItemModification,
  ) {
    return await podAxios.put<PODMallCartItemApi.Response.CartItemPutResponse>(
      `/cart-item/${id}`,
      payload,
    )
  },

  async postCartItem(
    payload:
      | PODMallCartItemApi.Request.SignatureWorkbookCartItemCreation
      | PODMallCartItemApi.Request.CustomWorkbookCartItemCreation
      | PODMallCartItemApi.Request.PrintDocumentCartItemCreation,
  ) {
    const { data } = await podAxios.post<PODMallCartItemApi.Response.CreateCartItem>(
      '/cart-item',
      payload,
    )
    return data
  },

  async deleteCartItem(ids: PODMallCartItemApi.Request.CartItemId[]) {
    await podAxios.delete(`/cart-item`, {
      params: { ids },
    })
  },

  async getCartItemPreview(cartItemId: number) {
    return await podAxios.get<common.PresignedFile>(`/cart-item/v2/download/${cartItemId}`)
  },

  // -- PreOrder
  async getPreOrder(params: PODMallPreOrderApi.Request.PreOrder) {
    const { data } = await podAxios.get<PODMallPreOrderApi.Response.PreOrderResponse>(
      '/pre-order',
      {
        params,
      },
    )
    return data
  },

  async postPreOrder(payload: PODMallPreOrderApi.Request.PreOrderCreation) {
    const { data } = await podAxios.post<PODMallPreOrderApi.Response.PreOrderCreationResponse>(
      '/pre-order',
      payload,
    )
    return data
  },

  async putPreOrder(payload: PODMallPreOrderApi.Request.PreOrderModification) {
    const { data } =
      await podAxios.put<PODMallPreOrderApi.Response.PreOrderDeiliveryAddressModificationResponse>(
        '/pre-order/delivery-address',
        payload,
      )

    return data
  },

  async getDeliveryLeadTime() {
    const { data } = await podAxios.get<PODMallPreOrderApi.Response.DeliveryLeadTimeResponse>(
      '/pre-order/delivery-lead-time',
    )
    return data
  },

  /* -- Order Start */
  async getOrders(pageQueryString: string) {
    const { data } = await podAxios.get<PaginationResponse<PODMallOrderApi.Response.DirectOrder[]>>(
      `/order${pageQueryString}`,
    )
    return data
  },

  /**
   * 학부모 결제 목록 조회(주문 내역)
   * @deprecated getParentOrdersV2로 마이그레이션 필요
   */
  async getParentOrders(params: PODMallOrderApi.Request.ParentOrder) {
    const { data } = await podAxios.get<PaginationResponse<PODMallOrderApi.Response.ParentOrder[]>>(
      '/parent-order',
      { params },
    )
    return data
  },

  /**
   * 학부모 결제 목록 조회(주문 내역)
   */
  async getParentOrdersV2(params: PODMallOrderApi.Request.ParentOrder) {
    const { data } = await podAxios.get<PaginationResponse<PODMallOrderApi.Response.ParentOrder[]>>(
      '/parent-order/v2',
      { params },
    )
    return data
  },

  /**
   * 학부모 결제 상세 조회
   * @deprecated getParentOrderDetailV2로 마이그레이션 필요
   */
  async getParentOrderDetail(paymentOrderId: PODMallOrderApi.Request.OrderId) {
    const { data } = await podAxios.get<PODMallOrderApi.Response.ParentOrderDetail>(
      `/parent-order/${paymentOrderId}`,
    )
    return data
  },

  /**
   * 학부모 결제 상세 조회
   */
  async getParentOrderDetailV2(paymentOrderId: PODMallOrderApi.Request.OrderId) {
    const { data } = await podAxios.get<PODMallOrderApi.Response.ParentOrderDetail>(
      `/parent-order/v2/${paymentOrderId}`,
    )
    return data
  },

  async getOrderItems(id: PODMallOrderApi.Request.OrderId) {
    const { data } = await podAxios.get<PODMallOrderApi.Response.OrderItem[]>(`/order/${id}/item`)
    return data
  },

  async cancelOrder(id: PODMallOrderApi.Request.OrderId) {
    return await podAxios.put(`/order/${id}/cancel`)
  },

  async getPrintOption() {
    return await podAxios.get<PODMallOrderApi.Response.PrintOption>('/print/option')
  },

  async getOrderItemPreview(orderItemId: number) {
    return await podAxios.get<common.PresignedFile>(`/order/v2/download/${orderItemId}`)
  },
  /* -- Order End */

  // -- Coupon
  async getCouponByPreOrderId(
    id: PODMallPreOrderApi.Request.PreOrder['id'],
    params?: { code: string } | undefined,
  ) {
    return await podAxios.get<PODMallCouponApi.Response.UserCouponResponse[]>(
      `/pre-order/${id}/user-coupon`,
      { params },
    )
  },

  async getCoupon(params?: { code?: string } | undefined) {
    return await podAxios.get<PODMallCouponApi.Response.UserCouponResponse[]>('/user-coupon', {
      params,
    })
  },

  async putUserCoupon(payload: PODMallCouponApi.Request.UserCouponRequest) {
    return await podAxios.put('/pre-order/user-coupon', payload)
  },

  async postUserCouponByCode(
    userCouponId: number,
    payload: PODMallCouponApi.Request.UserCouponByCodeRequest,
  ) {
    return await podAxios.post(`/user-coupon/${userCouponId}`, payload)
  },

  async getProductDiscountEvent() {
    return await podAxios.get<PODMallCouponApi.Response.ProductDiscountEvent>(
      '/user/product-discount-event',
    )
  },

  async getProductDiscountEventByPreOrder(preOrderId: string) {
    return await podAxios.get<PODMallPreOrderApi.Response.PreOrderProductDiscountEventResponse>(
      `/pre-order/${preOrderId}/product-discount-event`,
    )
  },

  async putProductDiscountEvent(
    preOrderId: string,
    params: PODMallPreOrderApi.Request.ProductDiscountEvent,
  ) {
    return await podAxios.put(`/pre-order/${preOrderId}/product-discount-event`, params)
  },

  putUserPoint: async (
    preOrderId: PODMallPreOrderApi.Request.PreOrder['id'],
    payload: PODMallPreOrderApi.Request.UserPoint,
  ) => {
    return await podAxios.put<void>(`/pre-order/${preOrderId}/point`, payload)
  },
  /* Payment Start */
  async postPaymentToss(payload: PODMallPreOrderApi.Request.Payment) {
    await maxios.post('/pod/payment', payload)
  },

  /* -- Parent Payment Start */
  // 학부모 결제 토큰 체크
  async getCheckPaymentToken(params: PODMallPaymentApi.Request.ParentPaymentParams) {
    return await podAxios.get<boolean>('/parent-payment/check-payment-token', {
      params,
    })
  },

  /**
   * 학부모 결제 정보 조회
   * @deprecated
   */
  async getParentPayment(params: PODMallPaymentApi.Request.ParentPaymentParams) {
    return await podAxios.get<PODMallPaymentApi.Response.ParentPayment>('/parent-payment', {
      params,
    })
  },

  /**
   * 학부모 결제 정보 조회 V2
   */
  async getParentPaymentV2(params: PODMallPaymentApi.Request.ParentPaymentParams) {
    return await podAxios.get<PODMallPaymentApi.Response.ParentPayment>('/parent-payment/v2', {
      params,
    })
  },

  /**
   * 학부모 결제 - 결제 승인
   * - 배송지 정보 업데이트
   * - 결제 승인 요청
   */
  async postParentPaymentToss(payload: ParentPaymentValidationDTO) {
    await podAxios.post<unknown, unknown, PODMallPaymentApi.Request.ParentPaymentValidationPayload>(
      '/parent-payment',
      payload,
    )
  },

  /**
   * 학부모 결제 - 직접결제
   * - 배송지 정보 업데이트
   * - 결제 승인 요청
   */
  async putParentPaymentDirect(payload: PODMallPaymentApi.Request.ParentPaymentValidationPayload) {
    await podAxios.put('/parent-payment/offline-payment', payload)
  },

  async putParentPayment(payload: PODMallPaymentApi.Request.ParentPaymentResendPayload) {
    await podAxios.put('/parent-order/resend', payload)
  },

  async sendParentPaymentRequest(payload: PODMallPaymentApi.Request.ParentPaymentSend) {
    return await podAxios.post<PODMallPaymentApi.Request.ParentPaymentSend>(
      '/parent-order',
      payload,
    )
  },

  async sendParentPaymentRequest_v2(payload: PODMallPaymentApi.Request.ParentPaymentSend_v2) {
    return await podAxios.post<PODMallPaymentApi.Request.ParentPaymentSend_v2>(
      '/parent-order/v2',
      payload,
    )
  },

  // 학부모 결제 현장결제
  async putParentPaymentToDirect(payload: PODMallPaymentApi.Request.ParentPaymentToDirectPayload) {
    return await podAxios.put('/parent-order/offline-payment', payload)
  },

  /* -- Parent Payment End */

  /* 정산 관련 API Start */
  /**
   * 정산 목록 조회
   * @deprecated - v2로 변경
   */
  async getGroupSettlementListByYearMonth(
    params: PODMallSettlementApi.Request.SettlementListParams,
  ) {
    return await podAxios.get<PODMallSettlementApi.Response.SettlementList>('/settlement', {
      params,
    })
  },

  /**
   * 정산 목록 조회 V2
   */
  async getGroupSettlementListByYearMonthV2(
    params: PODMallSettlementApi.Request.SettlementListParams,
  ) {
    return await podAxios.get<PODMallSettlementApi.Response.SettlementList>('/settlement/v2', {
      params,
    })
  },

  async downloadSettlementListExcel(params: PODMallSettlementApi.Request.SettlementListParams) {
    return await podAxios.get<Blob>('/settlement', {
      params,
      responseType: 'blob',
    })
  },

  async registerSettlementInfo(
    params: PODMallSettlementApi.Request.SettlementRegister,
    skipErrorToast?: boolean,
  ) {
    return await identifyAxios.post('/identification', params, { skipErrorToast })
  },
  /* 정산 관련 API End */

  /* KMM 상장&배너 주문 관련 API Start */
  async postOrder(payload: PODMallOrderApi.Request.KMMOrderCreation) {
    return await podAxios.post<PODMallPaymentApi.Response.KMMOrderCreation>('/order', payload)
  },
  async postKMMOrderPaymentToss(payload: PODMallPaymentApi.Request.KMMPaymentConfirmation) {
    return await podAxios.post('/payment/without-pre-order', payload)
  },

  async getKMMOrder(productId: number, statuses?: OrderStatusType[]) {
    return await podAxios.get<PODMallOrderApi.Response.KMMOrder[]>(`/kmm/order/${productId}`, {
      params: statuses ? { statuses } : undefined,
    })
  },

  /** 신규 사용자는 404. 호출부에서 null 로 정규화. */
  async getKMMAutoOrder() {
    const { data } = await podAxios.get<PODMallAutoOrderApi.Response.KMMAutoOrder>(
      '/user/kmm-award-auto-order',
    )
    return data
  },

  /** 서버가 호출 시 항상 enabled=true 로 마킹. */
  async saveKMMAutoOrderDeliveryAddress(payload: PODMallAutoOrderApi.Request.KMMAutoOrderUpsert) {
    const { data } = await podAxios.put<PODMallAutoOrderApi.Response.KMMAutoOrder>(
      '/user/kmm-award-auto-order',
      payload,
    )
    return data
  },

  /** 배송지·학원명은 보존 — 재신청 prefill 에 사용. */
  async cancelKMMAutoOrder() {
    const { data } = await podAxios.patch<PODMallAutoOrderApi.Response.KMMAutoOrder>(
      '/user/kmm-award-auto-order',
    )
    return data
  },
  /* KMM 상장&배너 주문 관련 API End */
}

export default podMallApi
